import type { GriffonneRankingEntry, GriffonneScoreLine } from './state.js';

/** Namespace socket de Griffonne. */
export const NS_GRIFFONNE = '/griffonne';

/** Events entrants (client → serveur) — acteur/rôle vérifiés serveur. */
export const GriffonneClientEvents = {
  /** dessinateur : choisit un mot parmi les 3 proposés. */
  PickWord: 'griffonne:pick_word',
  /** hôte (privé) : lance la partie au quorum. */
  Start: 'griffonne:start',
  /** rejouer une partie avec les joueurs présents. */
  Rematch: 'griffonne:rematch',
  // (dessin : draw:ops / draw:sync — socle `DrawEvents`, ADR 0014)
} as const;

/** Events sortants (serveur → client). Aucun ne porte le mot (sauf `word` en round_ended). */
export const GriffonneServerEvents = {
  Lobby: 'griffonne:lobby',
  /** 3 mots — émis AU DESSINATEUR SEUL (jamais broadcast/devineur), ADR 0003. */
  WordChoices: 'griffonne:word_choices',
  RoundStarted: 'griffonne:round_started',
  /** Un devineur a trouvé (sans le mot). */
  Solved: 'griffonne:solved',
  /** Indice progressif (lettres révélées) — révélation volontaire bornée. */
  Hint: 'griffonne:hint',
  /** Fin de manche : le mot est révélé (manche close). */
  RoundEnded: 'griffonne:round_ended',
  MatchEnded: 'griffonne:match_ended',
  /** Rappel du mot au dessinateur seul (room `principal:<kind>:<id>`). */
  YourWord: 'griffonne:your_word',
  /** Le joueur a été exclu de la partie (inactivité / déconnexion) — émis à lui seul. */
  Kicked: 'griffonne:kicked',
  /** Revanche prête : la room migre vers une NOUVELLE partie (même code). */
  RematchReady: 'griffonne:rematch_ready',
  Error: 'griffonne:error',
} as const;

/** Les 3 mots proposés — DESSINATEUR SEUL. */
export interface GriffonneWordChoicesPayload {
  words: string[];
  pickTimeSec: number;
}

export interface GriffonneRoundStartedPayload {
  round: number;
  totalRounds: number;
  drawerRef: string;
  /** Longueurs des mots (gabarit) — jamais les lettres. */
  wordShape: number[];
  drawTimeSec: number;
  /** ISO 8601 — échéance de la manche (compte à rebours). */
  endsAt: string;
}

export interface GriffonneSolvedPayload {
  ref: string;
  displayName: string;
  /** Rang de découverte (1 = premier). */
  order: number;
  /** Devineurs restant à trouver. */
  remainingGuessers: number;
}

/** Indice : par mot, une chaîne de lettres révélées et de `_`. */
export interface GriffonneHintPayload {
  mask: string[];
}

export interface GriffonneRoundEndedPayload {
  round: number;
  /** Le mot — révélé UNIQUEMENT ici (manche close, ADR 0003). */
  word: string;
  scores: GriffonneScoreLine[];
  totals: Array<{ ref: string; score: number }>;
  nextInSec: number;
}

export interface GriffonneMatchEndedPayload {
  ranking: GriffonneRankingEntry[];
}

/**
 * Revanche : une nouvelle partie a été créée (config + hôte + **code de partage
 * identiques** — le code est transféré depuis la partie terminée). Diffusé à toute
 * l'ancienne room pour que les participants présents la rejoignent ensemble.
 */
export interface GriffonneRematchReadyPayload {
  matchId: string;
}

export interface GriffonneYourWordPayload {
  word: string;
}

/** Motif d'exclusion d'un joueur (spec §5.2/§10). */
export type GriffonneKickReason = 'inactivity' | 'disconnection';

/**
 * Le joueur courant vient d'être exclu de la partie. Émis À LUI SEUL (room
 * `principal:<kind>:<id>`) ; le serveur l'a aussi retiré de la room + du roster.
 * `isPublic` oriente le bouton « Revenir » côté client (salon commun → nouveau
 * lobby ; partie privée → même partie, sinon message empathique).
 */
export interface GriffonneKickedPayload {
  reason: GriffonneKickReason;
  matchId: string;
  isPublic: boolean;
}

export interface GriffonneErrorPayload {
  code:
    | 'NOT_DRAWER'
    | 'NOT_YOUR_TURN'
    | 'BAD_STATE'
    | 'ROOM_FULL'
    | 'ALREADY_GUESSED'
    | 'INVALID_CHOICE';
  message: string;
}
